import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button'; // Assuming this exists from previous files
import { cn } from '@/lib/utils'; // Assuming standard shadcn/utils exist, if not I'll use inline or custom util

// Types
type DateRange = {
    start: Date | null;
    end: Date | null;
};

interface DateRangePickerProps {
    isOpen: boolean;
    onClose: () => void;
    range: DateRange;
    onChange: (range: DateRange) => void;
}

// Helpers
const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export const DateRangePicker = ({ isOpen, onClose, range, onChange }: DateRangePickerProps) => {
    // Current visible month in calendar
    const [currentMonth, setCurrentMonth] = useState(new Date());
    // Local selection state before confirming
    const [selection, setSelection] = useState<DateRange>(range);

    // Sync selection when opening
    useEffect(() => {
        if (isOpen) {
            setSelection(range);
            // If there's a start date, show that month, otherwise current
            setCurrentMonth(range.start || new Date());
        }
    }, [isOpen, range]);

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const handlePrevMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const handleNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    const isSameDay = (d1: Date, d2: Date) => {
        return d1.getDate() === d2.getDate() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getFullYear() === d2.getFullYear();
    };

    const handleDateClick = (day: number) => {
        const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

        // Logic for range selection
        // 1. If nothing selected, start = clicked
        // 2. If start selected but no end:
        //    - if clicked < start, new start = clicked, old start = end (swap)
        //    - else end = clicked
        // 3. If both selected, reset: start = clicked, end = null

        if (!selection.start || (selection.start && selection.end)) {
            setSelection({ start: clickedDate, end: null });
        } else if (selection.start && !selection.end) {
            if (clickedDate < selection.start) {
                setSelection({ start: clickedDate, end: selection.start });
            } else {
                setSelection({ start: selection.start, end: clickedDate });
            }
        }
    };

    const handleConfirm = () => {
        onChange(selection);
        onClose();
    };

    // Render Calendar Grid
    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentMonth);
        const firstDay = getFirstDayOfMonth(currentMonth);
        const slots = [];

        // Empty slots for days before first of month
        for (let i = 0; i < firstDay; i++) {
            slots.push(<div key={`empty-${i}`} className="h-9 w-9" />);
        }

        // Days
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

            let isSelected = false;
            let isRange = false;
            let isStart = false;
            let isEnd = false;

            if (selection.start && isSameDay(date, selection.start)) {
                isSelected = true;
                isStart = true;
            }
            if (selection.end && isSameDay(date, selection.end)) {
                isSelected = true;
                isEnd = true;
            }
            if (selection.start && selection.end && date > selection.start && date < selection.end) {
                isRange = true;
            }

            slots.push(
                <button
                    key={day}
                    onClick={() => handleDateClick(day)}
                    className={cn(
                        "h-9 w-9 text-sm rounded-full flex items-center justify-center transition-all relative z-10",
                        isSelected && "bg-gray-900 text-white font-medium hover:bg-gray-800",
                        !isSelected && !isRange && "text-gray-700 hover:bg-gray-100",
                        isRange && "bg-gray-100 text-gray-900 rounded-none",
                        // Visually connect range start/end
                        isStart && selection.end && "rounded-r-none",
                        isEnd && selection.start && "rounded-l-none"
                    )}
                >
                    {day}
                </button>
            );
        }

        return slots;
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 z-40" // Transparent backdrop to close
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-12 left-0 z-50 bg-white rounded-xl border border-gray-200 shadow-xl p-4 w-[320px]"
                    >
                        {/* Header: Month Nav */}
                        <div className="flex justify-between items-center mb-4">
                            <button onClick={handlePrevMonth} className="p-1 hover:bg-gray-100 rounded-full text-gray-500">
                                <ChevronLeft size={20} />
                            </button>
                            <span className="font-semibold text-gray-900">
                                {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                            </span>
                            <button onClick={handleNextMonth} className="p-1 hover:bg-gray-100 rounded-full text-gray-500">
                                <ChevronRight size={20} />
                            </button>
                        </div>

                        {/* Days Header */}
                        <div className="grid grid-cols-7 mb-2">
                            {days.map(d => (
                                <div key={d} className="h-8 flex items-center justify-center text-xs font-medium text-gray-400">
                                    {d}
                                </div>
                            ))}
                        </div>

                        {/* Calendar Grid */}
                        <div className="grid grid-cols-7 gap-y-1 mb-4">
                            {renderCalendar()}
                        </div>

                        {/* Helper Text */}
                        <p className="text-xs text-center text-gray-400 mb-4">
                            Seleccioná el rango de fechas para analizar el uso de IA
                        </p>

                        {/* Actions */}
                        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onClose}
                                className="text-gray-500 h-8"
                            >
                                Cancelar
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleConfirm}
                                disabled={!selection.start || !selection.end}
                                className="h-8 bg-gray-900 text-white hover:bg-gray-800"
                            >
                                Aplicar
                            </Button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};
